import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of, throwError } from "rxjs";
import { IInvoiceOutward, IInvoiceOutwardFilterRequest, Invoice, OutstandingDownloadRequest } from "./invoices.types";
import { HttpClient, HttpResponse } from "@angular/common/http";
import { catchError, map, switchMap, take, tap } from "rxjs/operators";
import { environment } from "environments/environment";
import moment from "moment";
import { BaseRespone, PaginationResult } from "app/core/models/common.response";
import { Payment } from "../payments/payments.types";

@Injectable({
    providedIn: 'root'
})
export class InvoicesService {
    // Private
    private _invoice: BehaviorSubject<Invoice | null> = new BehaviorSubject(null);
    private _invoices: BehaviorSubject<Invoice[] | null> = new BehaviorSubject(null);
    private _pagination: BehaviorSubject<PaginationResult | null> = new BehaviorSubject(null);
    private _invoiceNumbers: BehaviorSubject<any | null> = new BehaviorSubject(null);
    private _invoicePendingOutwards: BehaviorSubject<IInvoiceOutward[] | null> = new BehaviorSubject(null);
    private _payments: BehaviorSubject<Payment[] | null> = new BehaviorSubject(null);
    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient) {
    }

    /**
     * Getter for invoice
     */
    get invoice$(): Observable<Invoice> {
        return this._invoice.asObservable();
    }

    /**
     * Getter for invoices
     */
    get invoices$(): Observable<Invoice[]> {
        return this._invoices.asObservable();
    }

    /**
     * Getter for pagination
     */
    get pagination$(): Observable<PaginationResult> {
        return this._pagination.asObservable();
    }

    get invoiceNumbers$(): Observable<string> {
        return this._invoiceNumbers.asObservable();
    }

    get invoicePendingOutwards$(): Observable<IInvoiceOutward[]> {
        return this._invoicePendingOutwards.asObservable();
    }

    get payments$(): Observable<Payment[]> {
        return this._payments.asObservable();
    }

    /**
     * Get invoices
     */
    getInvoices(data): Observable<BaseRespone<{ pagination: PaginationResult; invoices: Invoice[] }>> {
        return this._httpClient.post<BaseRespone<{ pagination: PaginationResult; invoices: Invoice[] }>>(`${environment.baseUrl}/Invoice/list`, data).pipe(
            tap((response) => {
                this._pagination.next(response.result.pagination);
                this._invoices.next(response.result.invoices);
            })
        );
    }

    getInvoicePendingOutwards(data: IInvoiceOutwardFilterRequest): Observable<BaseRespone<IInvoiceOutward[]>> {
        return this._httpClient.post<BaseRespone<IInvoiceOutward[]>>(`${environment.baseUrl}/Invoice/GetOutWardList`, data).pipe(
            tap((response) => {
                this._invoicePendingOutwards.next(response.result);
            })
        );
    }

    /**
      * Create invoice
      */
    createInvoice(invoice: Invoice): Observable<BaseRespone<Invoice>> {
        return this._httpClient.post<BaseRespone<Invoice>>(`${environment.baseUrl}/Invoice`, invoice)
            .pipe(
                catchError((error) => {
                    return of(error.error);
                }),
                map((newProduct) => {
                    // Return the new product
                    return newProduct;
                })
            )
    }

    /**
     * Get board
     *
     * @param id
     */
    getInvoice(id: number): Observable<Invoice> {
        if (id > 0) {
            return this._httpClient.get(`${environment.baseUrl}/invoice/${id}`).pipe(
                catchError((error) => {
                    return of(error.error);
                }),
                map(response => response),
                tap(board => this._invoice.next(board.result))
            );
        }
        else {
            this._invoice.next(null);
            return of({} as Invoice);
        }
    }

    /**
     * Update the card
     *
     * @param id
     * @param card
     */
    updateInvoice(invoice: Invoice): Observable<BaseRespone<Invoice>> {
        return this._httpClient.put<BaseRespone<Invoice>>(`${environment.baseUrl}/invoice`, invoice).pipe(
            map((response) => {
                this._invoice.next(response.result);
                return response;
            }), catchError((error) => {
                return of(error.error);
            })
        );
    }


    deleteInvoice(id: number): Observable<BaseRespone<boolean>> {
        return this.invoices$.pipe(
            take(1),
            switchMap(invoices => this._httpClient.delete(`${environment.baseUrl}/invoice/${id}`).pipe(
                map((deleted: BaseRespone<boolean>) => {

                    // Find the index of the deleted product
                    const index = invoices.findIndex(item => item.id === id);

                    // Delete the product
                    invoices.splice(index, 1);

                    // Update the products
                    this._invoices.next(invoices);

                    // Return the deleted status
                    return deleted;
                })
            ))
        );
    }

    getNextNumber(isperfoma: boolean): Observable<BaseRespone<any>> {
        const data = {
            isPerfoma: isperfoma
        }
        return this._httpClient.post<BaseRespone<any>>(`${environment.baseUrl}/Invoice/GetLastInvoiceOrPerfomaInvoiceNumber`, data).pipe(
            catchError((error) => {
                return of(error.error);
            }),
            map(response => response),
            tap(board => this._invoiceNumbers.next(board.result))
        );
    }

    public exportInvoiceExcel(data): Observable<any> {
        const httpOptions: Object = {
            responseType: 'blob',
            observe: 'response'
        };
        return this._httpClient.post<any>(`${environment.baseUrl}/Invoice/excel/download`, data, httpOptions).pipe(
            tap((response) => {
                return response;
            })
        );
    }

    getInvoicePaymentList(invoiceId?: number): Observable<Payment[]> {
        if (invoiceId > 0) {
            return this._httpClient.get(`${environment.baseUrl}/invoice/payments/${invoiceId}`).pipe(
                catchError((error) => {
                    return of(error.error);
                }),
                map(response => response),
                tap(board => this._payments.next(board.result))
            );
        }
        else {
            this._payments.next(null);
            return of({} as Payment[]);
        }
    }

    public printInvoiceBill(invoiceid: number): Observable<any> {
        const httpOptions: Object = {
            responseType: 'blob',
            observe: 'response'
        };
        return this._httpClient.get<any>(`${environment.baseUrl}/Invoice/word/print/${invoiceid}`, httpOptions).pipe(
            tap((response) => {
                return response;
            })
        );
    }

    downloadExcelOutstanding(data: OutstandingDownloadRequest): Observable<HttpResponse<Blob>> {
        return this._httpClient.post(`${environment.baseUrl}/Invoice/ExcelOutstanding/download`, data, {
            responseType: 'blob',
            observe: 'response' 
        });
    }

}
